import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Check, Loader2, Plus, Settings, Trash2 } from 'lucide-react';

import { Button as UiButton } from '@/components/ui/button';
import { Checkbox as UiCheckbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type CommonProps = {
  className?: string;
};

export function Button({
  label,
  icon,
  severity,
  size,
  text,
  rounded,
  loading,
  className,
  ...props
}: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> & {
  label?: string;
  icon?: React.ReactNode | string;
  severity?: 'secondary' | 'danger';
  size?: 'small' | 'large';
  text?: boolean;
  rounded?: boolean;
  loading?: boolean;
}) {
  const variant = text
    ? 'ghost'
    : severity === 'danger'
      ? 'destructive'
      : severity === 'secondary'
        ? 'outline'
        : 'default';
  const mappedSize =
    size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'default';
  const iconNode =
    typeof icon === 'string' ? (
      icon.includes('pi-plus') ? (
        <Plus className="size-4" />
      ) : icon.includes('pi-trash') ? (
        <Trash2 className="size-4" />
      ) : icon.includes('pi-check') ? (
        <Check className="size-4" />
      ) : icon.includes('pi-cog') ? (
        <Settings className="size-4" />
      ) : null
    ) : (
      icon
    );

  return (
    <UiButton
      variant={variant}
      size={mappedSize}
      className={cn(rounded ? 'rounded-full' : '', className)}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : iconNode}
      {label}
    </UiButton>
  );
}

export function InputText({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & CommonProps) {
  return <Input {...props} className={className} />;
}

export function InputTextarea({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & CommonProps) {
  return (
    <textarea
      className={cn(
        'w-full rounded-lg border border-slate-300 bg-transparent px-2.5 py-1.5 text-sm outline-none',
        className,
      )}
      {...props}
    />
  );
}

export function Dropdown<T>({
  options = [],
  value,
  onChange,
  optionLabel = 'label',
  optionValue = 'value',
  placeholder,
  className,
}: {
  options?: T[];
  value?: unknown;
  onChange?: (e: { value: unknown }) => void;
  optionLabel?: string;
  optionValue?: string;
  placeholder?: string;
  className?: string;
}) {
  const normalized = useMemo(
    () =>
      options.map((opt) => {
        if (typeof opt === 'string') return { label: opt, value: opt };
        const record = opt as Record<string, unknown>;
        return {
          label: String(
            record[optionLabel] ?? record.label ?? record.name ?? '',
          ),
          value: String(record[optionValue] ?? record.value ?? record.id ?? ''),
        };
      }),
    [optionLabel, optionValue, options],
  );

  return (
    <select
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange?.({ value: e.target.value || null })}
      className={cn(
        'h-8 w-full rounded-lg border border-slate-300 bg-transparent px-2.5 text-sm outline-none',
        className,
      )}
    >
      <option value="">{placeholder ?? 'Select'}</option>
      {normalized.map((opt) => (
        <option key={String(opt.value)} value={String(opt.value)}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function ToggleButton({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (e: { value: boolean }) => void;
}) {
  return (
    <UiButton
      type="button"
      variant={checked ? 'default' : 'outline'}
      size="sm"
      onClick={() => onChange({ value: !checked })}
    >
      {checked ? 'On' : 'Off'}
    </UiButton>
  );
}

export function Divider() {
  return <hr className="my-3 border-slate-200" />;
}

export function Calendar({
  value,
  onChange,
  view,
  className,
}: {
  value?: Date | null;
  onChange?: (e: { value: Date | null }) => void;
  view?: 'month' | 'date';
  className?: string;
  placeholder?: string;
  readOnlyInput?: boolean;
  dateFormat?: string;
  showIcon?: boolean;
}) {
  const type = view === 'month' ? 'month' : 'date';
  const formatted =
    value instanceof Date && !Number.isNaN(value.getTime())
      ? view === 'month'
        ? `${value.getFullYear()}-${`${value.getMonth() + 1}`.padStart(2, '0')}`
        : value.toISOString().slice(0, 10)
      : '';

  return (
    <Input
      type={type}
      className={cn(
        'h-8 rounded-lg border-slate-300 bg-transparent text-sm',
        className,
      )}
      value={formatted}
      onChange={(e) => {
        if (!e.target.value) {
          onChange?.({ value: null });
          return;
        }
        const parsed =
          view === 'month'
            ? new Date(`${e.target.value}-01`)
            : new Date(e.target.value);
        onChange?.({ value: Number.isNaN(parsed.getTime()) ? null : parsed });
      }}
    />
  );
}

export function Chips({
  value,
  onChange,
  placeholder,
  className,
}: {
  value?: string[];
  onChange?: (e: { value: string[] }) => void;
  placeholder?: string;
  className?: string;
  separator?: string;
  allowDuplicate?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const items = value ?? [];
  return (
    <div
      className={cn(
        'flex flex-wrap gap-2 rounded-lg border border-slate-300 p-2',
        className,
      )}
    >
      {items.map((item) => (
        <span key={item} className="rounded bg-slate-100 px-2 py-0.5 text-xs">
          {item}
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ',') return;
          e.preventDefault();
          const next = draft.trim();
          if (!next) return;
          if (!items.includes(next)) onChange?.({ value: [...items, next] });
          setDraft('');
        }}
        placeholder={placeholder}
        className="min-w-[10rem] flex-1 bg-transparent text-sm outline-none"
      />
    </div>
  );
}

export function InputNumber({
  value,
  onValueChange,
  className,
  min,
}: {
  value?: number | null;
  onValueChange?: (e: { value: number | null }) => void;
  className?: string;
  min?: number;
}) {
  return (
    <Input
      type="number"
      className={cn(
        'h-8 rounded-lg border-slate-300 bg-transparent text-sm',
        className,
      )}
      min={min}
      value={value ?? ''}
      onChange={(e) => {
        const next = e.target.value;
        onValueChange?.({ value: next === '' ? null : Number(next) });
      }}
    />
  );
}

export function Tag({
  value,
  severity,
  className,
}: {
  value: string;
  severity?: 'success' | 'info' | 'warning';
  rounded?: boolean;
  className?: string;
}) {
  const bg =
    severity === 'success'
      ? 'bg-emerald-100 text-emerald-700'
      : severity === 'info'
        ? 'bg-sky-100 text-sky-700'
        : 'bg-amber-100 text-amber-700';
  return (
    <span
      className={cn('inline-flex rounded px-2 py-0.5 text-xs', bg, className)}
    >
      {value}
    </span>
  );
}

export type MenuItem = {
  label?: string;
  icon?: React.ReactNode;
  command?: () => void;
  items?: MenuItem[];
};

export type MenuHandle = {
  show: (e?: React.MouseEvent) => void;
};

export const Menu = forwardRef<
  MenuHandle,
  { model?: MenuItem[]; popup?: boolean; popupAlignment?: string }
>(function Menu({ model = [] }, ref) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [menuSize, setMenuSize] = useState({ width: 0, height: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const items = model.flatMap((group) => group.items ?? []);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const nextWidth = menuRef.current.offsetWidth;
    const nextHeight = menuRef.current.offsetHeight;
    setMenuSize((current) =>
      current.width === nextWidth && current.height === nextHeight
        ? current
        : { width: nextWidth, height: nextHeight },
    );
  }, [model, open]);

  const viewportWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  const edgePadding = 8;
  const measuredWidth = menuSize.width || 200;
  const measuredHeight =
    menuSize.height || Math.max(120, items.length * 36 + 12);
  const clampedLeft = viewportWidth
    ? Math.min(
        Math.max(position.x, edgePadding),
        Math.max(edgePadding, viewportWidth - measuredWidth - edgePadding),
      )
    : position.x;
  const clampedTop = viewportHeight
    ? Math.min(
        Math.max(position.y, edgePadding),
        Math.max(edgePadding, viewportHeight - measuredHeight - edgePadding),
      )
    : position.y;

  useImperativeHandle(ref, () => ({
    show(e?: React.MouseEvent) {
      if (e) setPosition({ x: e.clientX, y: e.clientY });
      setOpen(true);
    },
  }));
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50"
      onClick={() => setOpen(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        ref={menuRef}
        className="absolute min-w-44 rounded-lg border border-slate-200/80 bg-white/95 p-1 shadow-md backdrop-blur"
        style={{ top: clampedTop, left: clampedLeft }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            className="flex w-full appearance-none items-center gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm text-slate-700 shadow-none ring-0 transition-colors outline-none hover:bg-sky-50 hover:text-sky-700 focus-visible:bg-sky-50 focus-visible:text-sky-700"
            style={{ border: 0, background: 'transparent', boxShadow: 'none' }}
            onClick={() => {
              setOpen(false);
              item.command?.();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
});

export function TabPanel({
  children,
}: {
  header?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <>{children}</>;
}

export function TabView({
  children,
  activeIndex,
}: {
  children: React.ReactNode;
  renderActiveOnly?: boolean;
  activeIndex?: number;
}) {
  const tabs = Children.toArray(children).filter(isValidElement);
  const [active, setActive] = useState(0);
  useLayoutEffect(() => {
    if (typeof activeIndex !== 'number') return;
    const clamped = Math.max(
      0,
      Math.min(activeIndex, Math.max(0, tabs.length - 1)),
    );
    setActive(clamped);
  }, [activeIndex, tabs.length]);

  const resolvedActive = Math.max(
    0,
    Math.min(active, Math.max(0, tabs.length - 1)),
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab, idx) => (
          <UiButton
            key={idx}
            type="button"
            size="sm"
            variant={idx === resolvedActive ? 'default' : 'outline'}
            onClick={() => setActive(idx)}
          >
            {tab.props.header ?? `Tab ${idx + 1}`}
          </UiButton>
        ))}
      </div>
      <div>
        {isValidElement(tabs[resolvedActive])
          ? (tabs[resolvedActive].props as { children?: React.ReactNode })
              .children
          : null}
      </div>
    </div>
  );
}

export function Accordion({
  children,
  multiple = false,
  className,
}: {
  children: React.ReactNode;
  multiple?: boolean;
  className?: string;
}) {
  const items = Children.toArray(children).filter(isValidElement);
  const [open, setOpen] = useState<number[]>([]);
  return (
    <div className={cn('space-y-2', className)}>
      {items.map((item, idx) => {
        const isOpen = open.includes(idx);
        return cloneElement(item, {
          open: isOpen,
          onToggle: () => {
            setOpen((prev) =>
              multiple
                ? isOpen
                  ? prev.filter((v) => v !== idx)
                  : [...prev, idx]
                : isOpen
                  ? []
                  : [idx],
            );
          },
        });
      })}
    </div>
  );
}

export function AccordionTab({
  header,
  children,
  open,
  onToggle,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <button
        type="button"
        className="flex w-full appearance-none items-center justify-between border-0 bg-white px-3 py-2.5 text-left text-sm font-medium text-slate-800 shadow-none ring-0 outline-none focus:outline-none focus-visible:ring-0 focus-visible:outline-none"
        onClick={onToggle}
      >
        {header}
      </button>
      {open ? <div className="bg-white p-3 pt-2">{children}</div> : null}
    </div>
  );
}

export type CheckboxChangeEvent = { checked?: boolean };

export function Checkbox({
  checked,
  onChange,
  onClick,
  onMouseDown,
}: {
  inputId?: string;
  checked?: boolean;
  onChange?: (e: CheckboxChangeEvent) => void;
  onClick?: React.MouseEventHandler;
  onMouseDown?: React.MouseEventHandler;
}) {
  return (
    <span onClick={onClick} onMouseDown={onMouseDown}>
      <UiCheckbox
        checked={checked}
        onCheckedChange={(next) => onChange?.({ checked: !!next })}
      />
    </span>
  );
}

export function ColorPicker({
  value,
  onChange,
  inline,
}: {
  value?: string;
  onChange?: (e: { value: string }) => void;
  inline?: boolean;
}) {
  return (
    <input
      type="color"
      value={value ?? '#0096ff'}
      onChange={(e) => onChange?.({ value: e.target.value })}
      className={cn(
        'cursor-pointer rounded border border-slate-300 bg-white p-0.5',
        inline ? 'h-8 w-10' : 'h-8 w-full',
      )}
    />
  );
}

export function Dialog({
  visible,
  header,
  footer,
  onHide,
  style,
  children,
}: {
  visible: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  onHide?: () => void;
  style?: React.CSSProperties;
  breakpoints?: Record<string, string>;
  dismissableMask?: boolean;
  blockScroll?: boolean;
  children: React.ReactNode;
}) {
  if (!visible) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/20 p-4 py-6"
      onClick={onHide}
    >
      <div
        className="my-auto flex max-h-[calc(100dvh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-lg"
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {header ? <div className="mb-3">{header}</div> : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? <div className="mt-4">{footer}</div> : null}
      </div>
    </div>
  );
}
